<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ formTitle }}</h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body"><p>{{ formDescription }}</p></div>

                    <div class="popup-body popup-rows">
                        <div class="columns">
                            <div class="column is-one-fifth">
                                <label>{{ $t('stock_reconciliation.admin.column_location') }}</label>
                            </div>
                            <div class="column">
                                <strong>{{ location.city }}</strong><br/>
                                {{ location.street }} {{ location.house_number }}{{ location.addition }}<br/>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column is-one-fifth">
                                <label>{{ $t('stock_reconciliation.admin.current_stock_label') }}</label>
                            </div>
                            <div class="column">
                                <strong>{{ displayedCurrentStock }}</strong>
                                <!-- We always want to show recent stock even if it's zero in this case, since this
                                     popup is meant to assist during phone calls etc., it just seems better to show
                                     all relevant info -->
                                <small v-if="currentStockRecent > 0">
                                    <br/>{{ onTheWaySummary }}
                                </small>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column is-one-fifth">
                                <label>{{ $t('stock_reconciliation.admin.options') }}</label>
                            </div>
                            <div class="column">
                                <g-switch
                                    v-if="currentStockRecent > 0 && isMarkLostMode === false"
                                    name="include_in_transit_giftcards"
                                    :label="$tc('stock_reconciliation.admin.include_on_the_way_label', currentStockRecent, { count: currentStockRecent })"
                                    :subtitle="$t('stock_reconciliation.admin.include_on_the_way_subtitle')"
                                    @input="onIncludeGiftcardsOnTheirWayChanged"
                                />
                                <g-switch
                                    name="batch_mode"
                                    :label="$t('stock_reconciliation.admin.batch_mode_label')"
                                    :subtitle="$t('stock_reconciliation.admin.batch_mode_subtitle')"
                                    @input="onBatchModeChanged"
                                />
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <textarea
                                    class="textarea"
                                    rows="6"
                                    :placeholder="codesPlaceholder"
                                    v-model="codesInput"
                                    @input="onCodesChanged"
                                    :disabled="isVerifying"
                                ></textarea>
                            </div>
                        </div>

                        <div v-if="verifyError" class="columns">
                            <div class="column">
                                <div class="notification is-danger is-light">
                                    <p v-if="validationErrors.length === 0">{{ verifyError }}</p>
                                    <ul v-else class="mb-0">
                                        <li v-for="error in validationErrors" :key="error.code">
                                            <strong>{{ error.code }}</strong>: {{ error.detail }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div class="popup-footer">
                        <a @click.prevent="destroy" href="#" role="button">{{
                                $t('stock_reconciliation.admin.cancel')
                            }}</a>

                        <button
                            class="button is-success is-rounded"
                            :class="{ 'is-loading': isVerifying }"
                            :disabled="isVerifying"
                            @click.prevent="submit"
                        >
                            {{ submitButtonText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    props: {
        companyId: {
            type: [String, Number],
            required: true,
        },
        locationId: {
            type: String,
            required: true,
        },
        location: {
            type: Object,
            required: true,
        },
        currentStock: {
            type: Number,
            required: true,
        },
        currentStockRecent: {
            type: Number,
            required: true,
        },
        onSuccess: {
            type: Function,
            default: null,
        },
        operationMode: {
            type: String,
            default: 'inventory_scan',
        },
    },

    data() {
        return {
            batchMode: false,
            codesInput: '',
            includeGiftcardsOnTheirWay: false,
            isVerifying: false,
            verifyError: null,
            validationErrors: [],
        }
    },

    computed: {
        formTitle() {
            return this.isMarkLostMode
                ? this.$t('stock_reconciliation.admin.form_title_mark_lost')
                : this.$t('stock_reconciliation.admin.form_title_inventory_scan')
        },

        formDescription() {
            return this.isMarkLostMode
                ? this.$t('stock_reconciliation.admin.form_description_mark_lost')
                : this.$t('stock_reconciliation.admin.form_description_inventory_scan')
        },

        codesPlaceholder() {
            return this.isMarkLostMode
                ? this.$t('stock_reconciliation.admin.codes_placeholder_mark_lost')
                : this.$t('stock_reconciliation.admin.codes_placeholder_inventory_scan')
        },

        submitButtonText() {
            return this.isMarkLostMode
                ? this.$t('stock_reconciliation.admin.submit_button_mark_lost')
                : this.$t('stock_reconciliation.admin.submit_button_inventory_scan')
        },

        isMarkLostMode() {
            return this.operationMode === 'mark_lost'
        },

        effectiveIncludeGiftcardsOnTheirWay() {
            return this.isMarkLostMode || this.includeGiftcardsOnTheirWay
        },

        displayedCurrentStock() {
            return this.effectiveIncludeGiftcardsOnTheirWay
                ? this.currentStock
                : this.currentStock - this.currentStockRecent
        },

        onTheWaySummary() {
            const key = this.effectiveIncludeGiftcardsOnTheirWay
                ? 'stock_reconciliation.admin.on_the_way_included'
                : 'stock_reconciliation.admin.on_the_way_excluded'

            return this.$tc(key, this.currentStockRecent, {
                count: this.currentStockRecent,
            })
        },

        parsedCodes() {
            if (!this.codesInput.trim()) {
                return []
            }

            return this.codesInput
                .split(/[\n,;]+/)
                .map(code => code.trim())
                .filter(code => code.length > 0)
        },
    },

    methods: {
        onBatchModeChanged(event, value) {
            this.batchMode = value
            this.onCodesChanged()
        },

        onIncludeGiftcardsOnTheirWayChanged(event, value) {
            this.includeGiftcardsOnTheirWay = value
            this.onCodesChanged()
        },

        onCodesChanged() {
            this.verifyError = null
            this.validationErrors = []
        },

        async verify() {
            this.isVerifying = true
            this.verifyError = null
            this.validationErrors = []

            try {
                const response = await axios.post(
                    `/api/admin/company/${this.companyId}/stock-reconciliation/verify`,
                    {
                        location_id: this.locationId,
                        giftcard_codes: this.parsedCodes,
                        batch_mode: this.batchMode,
                        include_in_transit_giftcards: this.effectiveIncludeGiftcardsOnTheirWay,
                        operation_mode: this.operationMode,
                    }
                )

                return response.data
            } catch (error) {
                if (error.response && error.response.data) {
                    const errorData = error.response.data
                    this.verifyError = errorData.message || this.$t('stock_reconciliation.admin.verify_failed')
                    this.validationErrors = errorData.errors || []
                } else {
                    this.verifyError = this.$t('stock_reconciliation.admin.verify_failed')
                    this.validationErrors = []
                }
                throw error
            } finally {
                this.isVerifying = false
            }
        },

        async submit() {
            try {
                const verificationResult = await this.verify()

                this.openConfirmationModal(verificationResult)
            } catch (error) {
                // The verify step already maps the response to the inline error state.
            }
        },

        openConfirmationModal(verificationResult) {
            const hasCorrections = verificationResult.missing_count > 0

            this.$popup('Confirmation', {
                title: this.formTitle,
                text: this.buildConfirmationText(verificationResult),
                enableMarkdown: true,
                confirmationButtonText: hasCorrections
                    ? this.submitButtonText
                    : this.$t('stock_reconciliation.close'),
                onConfirmation: () => {
                    if (hasCorrections === false) {
                        this.destroy()

                        return Promise.resolve()
                    }

                    return this.createCorrection()
                },
            })
        },

        buildConfirmationText(verificationResult) {
            if (verificationResult.missing_count === 0) {
                return this.$tc(
                    'stock_reconciliation.admin.confirm_no_changes_text',
                    verificationResult.stock_after_correction,
                    {count: verificationResult.stock_after_correction}
                )
            }

            return [
                this.$tc('stock_reconciliation.admin.verify_text_mark', verificationResult.missing_count, {
                    count: verificationResult.missing_count,
                }),
                this.$tc('stock_reconciliation.admin.verify_text_stock_after', verificationResult.stock_after_correction, {
                    count: verificationResult.stock_after_correction,
                }),
            ].join('\n\n')
        },

        async createCorrection() {
            try {
                const response = await axios.post(
                    `/api/admin/company/${this.companyId}/stock-reconciliation`,
                    {
                        location_id: this.locationId,
                        giftcard_codes: this.parsedCodes,
                        batch_mode: this.batchMode,
                        include_in_transit_giftcards: this.effectiveIncludeGiftcardsOnTheirWay,
                        operation_mode: this.operationMode,
                    }
                )

                if (response.data && response.data.message) {
                    this.$buefy.toast.open({
                        message: response.data.message,
                        type: 'is-success',
                    })
                }

                if (this.onSuccess) {
                    this.onSuccess()
                }

                this.destroy()

                return response
            } catch (error) {
                if (error.response && error.response.data) {
                    const errorData = error.response.data
                    this.verifyError = errorData.message || this.$t('stock_reconciliation.admin.submit_failed')
                    this.validationErrors = errorData.errors || []
                } else {
                    this.verifyError = this.$t('stock_reconciliation.admin.submit_failed')
                    this.validationErrors = []
                }

                throw error
            }
        },
    },
}
</script>
